import { useEffect, useState } from "react";
import { api, wsUrl } from "@/lib/api";

/**
 * useChannelSettings — encapsulates all API polls + WebSocket telemetry a
 * ChannelSettingsDialog needs. Returns the loaded state and the setters the
 * dialog needs to expose to its panels.
 */
export default function useChannelSettings({ open, channel }) {
  const [engineAdapters, setEngineAdapters] = useState([]);
  const [pipelineHealth, setPipelineHealth] = useState(null);
  const [pipelineSegments, setPipelineSegments] = useState([]);
  const [rotations, setRotations] = useState([]);
  const [engineNodes, setEngineNodes] = useState([]);
  const [migrations, setMigrations] = useState([]);
  const [regionsData, setRegionsData] = useState(null);
  const [planLimits, setPlanLimits] = useState(null);
  const [kmsBackends, setKmsBackends] = useState(["local"]);
  const [engineHello, setEngineHello] = useState(null);
  const [engineStatus, setEngineStatus] = useState(null);

  // Adapters (one-off per channel open)
  useEffect(() => {
    if (!channel) return;
    api.get("/engine/adapters").then((r) => setEngineAdapters(r.data.adapters || [])).catch(() => { /* noop */ });
  }, [channel]);

  // Polled state — pipeline / rotations / nodes / regions
  useEffect(() => {
    if (!open || !channel) return;
    let alive = true;
    const load = async () => {
      try {
        const [h, segs, rots, nodes, migs, regs] = await Promise.all([
          api.get(`/channels/${channel.id}/publish/health`),
          api.get(`/channels/${channel.id}/publish/segments`, { params: { limit: 8 } }),
          api.get(`/channels/${channel.id}/cdn/rotations`),
          api.get(`/engine/nodes`),
          api.get(`/channels/${channel.id}/engine/migrations`),
          api.get(`/channels/${channel.id}/cdn/regions`),
        ]);
        if (!alive) return;
        setPipelineHealth(h.data);
        setPipelineSegments(segs.data.segments || []);
        setRotations(rots.data.rotations || []);
        setEngineNodes(nodes.data.nodes || []);
        setMigrations(migs.data.migrations || []);
        setRegionsData(regs.data);
      } catch { /* noop */ }
    };
    load();
    const iv = setInterval(load, 3000);
    api.get("/tenants/current/plan-limits").then((r) => setPlanLimits(r.data)).catch(() => { /* noop */ });
    api.get("/kms/backends").then((r) => setKmsBackends(r.data.backends || ["local"])).catch(() => { /* noop */ });
    return () => { alive = false; clearInterval(iv); };
  }, [open, channel]);

  // Realtime engine telemetry
  useEffect(() => {
    if (!open || !channel) return;
    let ws;
    try { ws = new WebSocket(wsUrl(`/api/ws/engine/${channel.id}`)); } catch { return; }
    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        if (msg.type === "hello") setEngineHello(msg);
        else if (msg.type === "telemetry") setEngineStatus(msg);
      } catch { /* noop */ }
    };
    return () => { try { ws.close(); } catch { /* noop */ } };
  }, [open, channel]);

  const refreshRotations = async () => {
    if (!channel) return;
    try {
      const r = await api.get(`/channels/${channel.id}/cdn/rotations`);
      setRotations(r.data.rotations || []);
    } catch { /* noop */ }
  };

  return {
    engineAdapters,
    pipelineHealth,
    pipelineSegments,
    rotations,
    engineNodes,
    migrations,
    regionsData,
    planLimits,
    kmsBackends,
    engineHello,
    engineStatus,
    setEngineHello,
    setEngineStatus,
    setPipelineHealth,
    setPipelineSegments,
    refreshRotations,
  };
}
